import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { NotFoundPage } from "@pages/not-found-page";
import { useAppParams } from "@router/router-helper";
import { appStateSelector } from "@store/app/app-selector";
import { useAppSelector } from "@store/store-helper";
import { trackOpenProject } from "@utils/project-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getBooleanQueryParam } from "@utils/url-utils";
import { useEffect } from "react";

/**
 * Redirects to the Viewer's Data Management editor if the project ID is defined.
 * Otherwise it shows an error page.
 * This redirect is necessary because some users may still have deep links to the Dashboard's
 * removed Data Management editor.
 */
export function DataManagement(): JSX.Element {
  const { trackAsyncEvent } = useTrackEvent();
  const { navigateToDataManagement } = useAppNavigation();
  const { projectId } = useAppParams();

  // Provide the supported URL parameters to the Data Management editor in the Viewer.
  const {
    isDevModeEnabled,
    isAlphaTestingEnabled,
    isBetaTestingEnabled,
  } = useAppSelector(appStateSelector);

  const shouldShowUpload = getBooleanQueryParam(globalThis.location.search, "importFromViewer") ||
    getBooleanQueryParam(globalThis.location.search, "showUpload");

  const shouldShowPublish = getBooleanQueryParam(globalThis.location.search, "publishPanel") ||
    getBooleanQueryParam(globalThis.location.search, "showPublish");

  useEffect(() => {
    if (projectId) {
      void trackOpenProject({
        openFrom: "dataManagement-redirect",
        numberOfMembers: undefined,
        openTarget: ProjectLaunchTarget.sphereViewer,
        trackAsyncEvent,
      });

      navigateToDataManagement({
        projectId,
        shouldEnableDev: isDevModeEnabled,
        shouldEnableAlpha: isAlphaTestingEnabled,
        shouldEnableBeta: isBetaTestingEnabled,
        shouldShowUpload,
        shouldShowPublish,
      });
    }
  }, [
    isAlphaTestingEnabled,
    isBetaTestingEnabled,
    isDevModeEnabled,
    navigateToDataManagement,
    projectId,
    shouldShowPublish,
    shouldShowUpload,
    trackAsyncEvent,
  ]);

  if (!projectId) {
    return <NotFoundPage />;
  }
  // Let the Viewer carry out further access checks for the project.

  // Dummy return value while redirecting.
  return <div>&nbsp;</div>;
}
